import Swal from "sweetalert2";
import { getCurrentClientDbName } from "@/api/wencesApi";
import { dbNameMatchesTenant } from "@/utils/dbNameMatchesTenant";

const HOOK_VALIDATION_MARKER = "Errores de validación";
const COLLECT_FILE_MARKER = "archivo del cobro";
/** Marcador estable del hook de notacomercial; el texto visible sale de i18n. */
const NOTA_COMERCIAL_NO_FILE_MARKER = "NOTA_COMERCIAL_RECEIVED_WITHOUT_FILE";
/** Marcador estable del hook (duntower); el texto visible sale de i18n. */
const PENDING_AMOUNT_MARKER = "Te queda un monto pendiente a aplicar";
/** Marcador del aviso de pago/cobro aplicado a una operación ajena a su contacto. */
const CONTACT_OUTSIDE_OPERATION_MARKER = "CONTACT_OUTSIDE_OPERATION";
/** Marcador del aviso de cobro por encima del pendiente del embarque (#476, collectShippingPendingCap). */
const COLLECT_OVER_PENDING_MARKER = "COLLECT_OVER_PENDING";
/** Lo aplicado supera el monto del cobro (hook_validate_collect): tope duro, no aviso (#476). */
const COLLECT_APPLIED_OVER_AMOUNT_MARKER = "no se puede aplicar más de lo cobrado";
/** Línea aplicada a un embarque que ya no tiene nada pendiente (collectShippingPendingCap): tope duro. */
const COLLECT_SHIPPING_FULLY_COLLECTED_MARKER = "ya está cobrado por completo";
/** Línea que sobra: el pendiente del embarque ya lo cubren las otras líneas del cobro. Tope duro. */
const COLLECT_SHIPPING_COVERED_MARKER = "ya lo cubren las otras líneas";

export function isCollectFormFileEmpty(fileVal) {
  if (fileVal === null || fileVal === undefined || fileVal === "") return true;
  if (Array.isArray(fileVal)) return fileVal.length === 0;
  if (typeof fileVal === "object") {
    const keys = Object.keys(fileVal).filter((k) => !["_id", "__v"].includes(k));
    return keys.length === 0 || keys.every((k) => isCollectFormFileEmpty(fileVal[k]));
  }
  return false;
}

export function isDuntowerTenant() {
  return dbNameMatchesTenant(getCurrentClientDbName(), "duntower");
}

export function getApiErrorData(error) {
  return (error && error.response && error.response.data) || null;
}

/** Mensaje legible de error HTTP (p. ej. bloqueo de borrado por pagos/cobros). */
export function getApiErrorMessage(error, fallback = "") {
  const data = getApiErrorData(error);
  const fromData =
    data &&
    (typeof data.error === "string"
      ? data.error
      : typeof data.message === "string"
        ? data.message
        : "");
  if (fromData && fromData.trim()) return fromData.trim();
  if (error && typeof error.message === "string" && error.message.trim()) {
    return error.message.trim();
  }
  return fallback || "";
}

export function isHookValidationError(errorData) {
  const msg = errorData && errorData.message;
  return typeof msg === "string" && msg.includes(HOOK_VALIDATION_MARKER);
}

/** 401/403 o mensaje típico de JWT / sesión vencida. */
export function isAuthSessionError(error) {
  const status = error && error.response && error.response.status;
  if (status === 401 || status === 403) return true;
  const data = getApiErrorData(error);
  const msg = String(
    (data && (data.message || data.error)) || (error && error.message) || ""
  ).toLowerCase();
  return (
    msg.includes("jwt expired") ||
    msg.includes("jwt malformed") ||
    msg.includes("token expired") ||
    msg.includes("invalid token") ||
    msg.includes("unauthorized")
  );
}

/**
 * Feedback del segundo intento (forceSave) cuando falla.
 * Sin esto el modal de validación parece “no guardar”.
 * @param {(key: string, params?: object) => string} t
 * @param {unknown} error
 */
export async function showForceSaveRetryError(t, error) {
  if (isAuthSessionError(error)) {
    await Swal.fire({
      icon: "warning",
      title: t("validationForceSaveSessionTitle"),
      text: t("validationForceSaveSessionText"),
      confirmButtonText: t("ok") || "OK",
    });
    return;
  }
  const errorData = getApiErrorData(error);
  const text =
    (errorData && (errorData.message || errorData.error)) ||
    (error && error.message) ||
    t("validationForceSaveGenericText");
  await Swal.fire({
    icon: "error",
    title: t("error") || "Error",
    text,
    confirmButtonText: t("ok") || "OK",
  });
}

export function isCollectFileHookValidationError(errorData) {
  const msg = errorData && errorData.message;
  return isHookValidationError(errorData) && String(msg).includes(COLLECT_FILE_MARKER);
}

export function shouldUseCollectFileValidationPrompt(modelName, errorMessage) {
  if (String(modelName || "").toLowerCase() !== "collect") return false;
  return String(errorMessage || "").includes(COLLECT_FILE_MARKER);
}

/** Nota que nos emite el contacto y se guarda sin adjunto (aviso blando). */
export function isNotaComercialWithoutFileError(errorMessage) {
  return String(errorMessage || "").includes(NOTA_COMERCIAL_NO_FILE_MARKER);
}

/** Pago o cobro cuyo contacto no participa de la operación aplicada (aviso blando). */
export function isContactOutsideOperationError(errorMessage) {
  return String(errorMessage || "").includes(CONTACT_OUTSIDE_OPERATION_MARKER);
}

/**
 * Aviso: el contacto del pago/cobro no participa de la operación a la que se aplica.
 * Puede ser un tercero que paga o cobra por otro, así que se deja guardar igual.
 * @param {(key: string, params?: object) => string} t
 * @param {string} errorMessage mensaje del hook («… CONTACT_OUTSIDE_OPERATION: X no participa de OP…»
 *   o, en un cobro, «X no es el cliente de OP…»)
 * @returns {Promise<boolean>}
 */
export async function promptContactOutsideOperationForceSave(t, errorMessage) {
  const detail = String(errorMessage || "").split(`${CONTACT_OUTSIDE_OPERATION_MARKER}:`)[1] || "";
  // El cobro exige al cliente («no es el cliente de»); el pago, que participe.
  const match = detail.match(/^\s*(.*?) (no participa de|no es el cliente de) (.*)$/);
  const notClient = !!match && match[2] === "no es el cliente de";
  const result = await Swal.fire({
    icon: "warning",
    title: t(notClient ? "collectContactNotClientTitle" : "contactOutsideOperationTitle"),
    text: t(notClient ? "collectContactNotClientText" : "contactOutsideOperationText", {
      contact: match ? match[1] : "",
      operations: match ? match[3] : "",
    }),
    showCancelButton: true,
    confirmButtonText: t("validationSaveAnyway"),
    cancelButtonText: t("contactOutsideOperationCancel"),
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#dc3545",
    heightAuto: false,
  });
  return !!result.isConfirmed;
}

/**
 * Guarda y, si el back avisa que el contacto no participa de la operación o que el cobro
 * supera el pendiente del embarque, pregunta y reintenta con forceSave. Para los modales
 * que no pasan por promptHookValidationForceSave.
 * @param {(key: string, params?: object) => string} t
 * @param {(forceSave: boolean) => Promise<any>} save
 * @returns {Promise<any|null>} la respuesta, o null si el usuario no quiso guardar
 */
export async function saveConfirmingContactOutsideOperation(t, save) {
  try {
    return await save(false);
  } catch (error) {
    const message = getApiErrorMessage(error);
    let confirmed;
    if (isCollectOverPendingError(message)) {
      confirmed = await promptCollectOverPendingForceSave(t, message);
    } else if (isContactOutsideOperationError(message)) {
      confirmed = await promptContactOutsideOperationForceSave(t, message);
    } else {
      throw error;
    }
    if (!confirmed) return null;
    return save(true);
  }
}

export function isPendingAmountHookValidationError(errorMessage) {
  return String(errorMessage || "").includes(PENDING_AMOUNT_MARKER);
}

/** Cobro que supera el pendiente de un embarque (aviso blando). */
export function isCollectOverPendingError(errorMessage) {
  return String(errorMessage || "").includes(COLLECT_OVER_PENDING_MARKER);
}

function escapeSwalHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Una entrada por embarque del aviso del back:
 * «COLLECT_OVER_PENDING: Se aplican X a «OP…», que tiene pendiente Y (excedente Z)».
 */
function parseCollectOverPending(errorMessage) {
  const re = new RegExp(
    `${COLLECT_OVER_PENDING_MARKER}: Se aplican (.+?) a «(.*?)», que tiene pendiente (.+?) \\(excedente (.+?)\\)`,
    "g"
  );
  const items = [];
  let match;
  while ((match = re.exec(String(errorMessage || ""))) !== null) {
    items.push({ applied: match[1], shipping: match[2], pending: match[3], excess: match[4] });
  }
  return items;
}

/**
 * Aviso: el cobro supera el pendiente de uno o más embarques. Si se guarda igual, el back
 * aplica hasta el pendiente y el excedente queda sin aplicar, a favor del contacto.
 * Va antes que el aviso del archivo y lo menciona: los dos se saltean con el mismo
 * «Guardar de todos modos», así que la persona tiene que verlos juntos.
 * @param {(key: string, params?: object) => string} t
 * @param {string} errorMessage
 * @returns {Promise<boolean>}
 */
export async function promptCollectOverPendingForceSave(t, errorMessage) {
  const lines = parseCollectOverPending(errorMessage).map((item) =>
    escapeSwalHtml(t("collectOverPendingLine", item))
  );
  const noFile = String(errorMessage || "").includes(COLLECT_FILE_MARKER)
    ? `<p class="mb-0">${escapeSwalHtml(t("collectOverPendingAlsoNoFile"))}</p>`
    : "";
  const result = await Swal.fire({
    icon: "warning",
    title: t("collectOverPendingTitle"),
    html:
      `<div class="text-left">` +
      `<p>${lines.join("<br>")}</p>` +
      `<p>${escapeSwalHtml(t("collectOverPendingFooter"))}</p>` +
      noFile +
      `</div>`,
    showCancelButton: true,
    confirmButtonText: t("validationSaveAnyway"),
    cancelButtonText: t("Cancel"),
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#dc3545",
    heightAuto: false,
  });
  return !!result.isConfirmed;
}

function buildValidationErrorHtml(errorMessage, t) {
  const body = String(errorMessage || "")
    .replace(PENDING_AMOUNT_MARKER, t("validationPendingAmountToApply"))
    .replace(/Los campos /g, "• <strong>Los campos </strong>")
    .replace(/El campo /g, "• <strong>El campo </strong>")
    .replace(/, /g, "</strong><br>");
  return `<div class="text-left">
    <p><strong>${t("validationSaveFailedIntro")}</strong></p>
    <div class="mt-3">${body}</div>
  </div>`;
}

/**
 * Aviso unificado: cobro sin archivo (Duntower) — mismas claves que QuickAddCollectModal.
 * @param {(key: string, params?: object) => string} t
 * @returns {Promise<boolean>}
 */
export async function promptCollectFileRequiredForceSave(t) {
  const result = await Swal.fire({
    icon: "warning",
    title: t("financialSummary.quickAddCollectFileRequiredDuntowerTitle"),
    text: t("financialSummary.quickAddCollectFileRequiredDuntowerText"),
    showCancelButton: true,
    confirmButtonText: t("validationSaveAnyway"),
    cancelButtonText: t("financialSummary.close"),
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#dc3545",
  });
  return !!result.isConfirmed;
}

/**
 * Aviso: monto total no aplicado del todo (Duntower payment/collect).
 * @param {(key: string, params?: object) => string} t
 * @returns {Promise<boolean>}
 */
export async function promptPendingAmountForceSave(t) {
  const result = await Swal.fire({
    icon: "warning",
    title: t("validationSaveFailedTitle"),
    text: t("validationPendingAmountToApply"),
    showCancelButton: true,
    confirmButtonText: t("validationSaveAnyway"),
    cancelButtonText: t("validationUnderstood"),
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#dc3545",
  });
  return !!result.isConfirmed;
}

/**
 * Error de validación del hook con opción forceSave.
 * @param {(key: string, params?: object) => string} t
 * @param {string} errorMessage
 * @param {{ modelName?: string }} [options]
 * @returns {Promise<boolean>}
 */
export async function promptHookValidationForceSave(t, errorMessage, options = {}) {
  const { modelName } = options;
  // Aplicar más de lo cobrado, o a un embarque ya cobrado del todo, es un tope duro: «Guardar
  // de todos modos» reintentaría y el back lo volvería a rechazar. Va primero: aunque vengan avisos blandos, así no se puede guardar.
  if (
    String(errorMessage || "").includes(COLLECT_APPLIED_OVER_AMOUNT_MARKER) ||
    String(errorMessage || "").includes(COLLECT_SHIPPING_FULLY_COLLECTED_MARKER) ||
    String(errorMessage || "").includes(COLLECT_SHIPPING_COVERED_MARKER)
  ) {
    await Swal.fire({
      icon: "error",
      title: t("validationSaveFailedTitle"),
      html: buildValidationErrorHtml(errorMessage, t),
      confirmButtonText: t("validationUnderstood"),
      confirmButtonColor: "#dc3545",
    });
    return false;
  }
  // Antes que el del archivo: si no, «Guardar de todos modos» saltea el excedente sin mostrarlo.
  if (isCollectOverPendingError(errorMessage)) {
    return promptCollectOverPendingForceSave(t, errorMessage);
  }
  if (shouldUseCollectFileValidationPrompt(modelName, errorMessage)) {
    return promptCollectFileRequiredForceSave(t);
  }
  // Moneda distinta a la del gasto: es un tope duro, no un aviso. Ofrecer «guardar
  // igual» llevaría a un segundo intento que el backend vuelve a rechazar.
  if (String(errorMessage || "").includes("NOTA_COMERCIAL_CURRENCY_MISMATCH")) {
    const { notaComercialCurrencyMismatchAlert } = await import(
      "@/modules/nota_comercial_module/utils/notaComercialSaveErrors"
    );
    await notaComercialCurrencyMismatchAlert(t);
    return false;
  }
  if (isNotaComercialWithoutFileError(errorMessage)) {
    const { promptNotaComercialMissingFile } = await import(
      "@/modules/nota_comercial_module/utils/notaComercialSaveErrors"
    );
    return promptNotaComercialMissingFile(t);
  }
  if (isContactOutsideOperationError(errorMessage)) {
    return promptContactOutsideOperationForceSave(t, errorMessage);
  }
  if (isPendingAmountHookValidationError(errorMessage)) {
    return promptPendingAmountForceSave(t);
  }
  const result = await Swal.fire({
    icon: "error",
    title: t("validationSaveFailedTitle"),
    html: buildValidationErrorHtml(errorMessage, t),
    showCancelButton: true,
    confirmButtonText: t("validationSaveAnyway"),
    cancelButtonText: t("validationUnderstood"),
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#dc3545",
  });
  return !!result.isConfirmed;
}

/**
 * Antes de POST collect en Duntower si falta archivo.
 * @param {(key: string, params?: object) => string} t
 * @param {{ file?: unknown }} mergedPre
 * @param {{ showsFileField?: boolean }} [options]
 * @returns {Promise<boolean>}
 */
export async function confirmDuntowerCollectSaveWithoutFile(t, mergedPre, options = {}) {
  const { showsFileField = true } = options;
  if (!showsFileField) return true;
  if (!isDuntowerTenant()) return true;
  if (!isCollectFormFileEmpty(mergedPre && mergedPre.file)) return true;
  return promptCollectFileRequiredForceSave(t);
}

/**
 * Antes de guardar un gasto del embarque (Duntower) sin «Related file»: avisa y deja
 * guardar igual o volver a adjuntarlo.
 *
 * Es un aviso del formulario y no del back: los gastos también los crean el autoload y
 * los hooks, que no tienen a quién preguntarle.
 * @param {(key: string, params?: object) => string} t
 * @param {string} modelName
 * @param {{ file?: unknown }} form
 * @returns {Promise<boolean>} true si se puede guardar
 */
export async function confirmDuntowerExpenseSaveWithoutFile(t, modelName, form) {
  if (String(modelName || "").toLowerCase() !== "shipping_expenses") return true;
  if (!isDuntowerTenant()) return true;
  if (!isCollectFormFileEmpty(form && form.file)) return true;
  const result = await Swal.fire({
    icon: "warning",
    title: t("expenseFileMissingTitle"),
    text: t("expenseFileMissingText"),
    showCancelButton: true,
    confirmButtonText: t("validationSaveAnyway"),
    cancelButtonText: t("expenseFileMissingCancel"),
    confirmButtonColor: "#28a745",
    cancelButtonColor: "#dc3545",
    heightAuto: false,
  });
  return !!result.isConfirmed;
}
